import { useRef, useEffect, useState, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox, MeshWobbleMaterial } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT, CHARACTER_FACE_COLORS } from './constants';
import { Face } from './shared/Face';
import { WizardHat, RocketJetpack, CoolGlasses } from './shared/Accessories3D';
import type { Position, Direction } from '@/types/game';

type CharacterState = 'entering' | 'playing' | 'won' | 'lost' | 'exiting';

// Fly-away sub-phases matching the 2D version
type FlyAwayPhase = 'idle' | 'hop' | 'spin' | 'launch' | 'gone';

// Entrance sub-phases
type EntrancePhase = 'hidden' | 'appear' | 'hop1' | 'hop2' | 'landing';

interface Character3DWebGLProps {
  position: Position;
  direction: Direction | null;
  hasCollided: boolean;
  isMoving: boolean;
  characterId: string;
  totalCols: number;
  totalRows: number;
  gameState: CharacterState;
  equippedProps?: string[];
}

function getExpression(gameState: CharacterState, flyPhase: FlyAwayPhase, hasCollided: boolean): 'smile' | 'sad' | 'shock' | 'surprise' | 'hurt' | 'celebrate' {
  if (hasCollided) return 'hurt';
  switch (gameState) {
    case 'won': return 'celebrate';
    case 'exiting':
      return flyPhase === 'spin' || flyPhase === 'launch' ? 'celebrate' : 'shock';
    case 'lost': return 'sad';
    case 'entering': return 'surprise';
    default: return 'smile';
  }
}

export default function Character3DWebGL({
  position,
  direction,
  hasCollided,
  isMoving,
  characterId,
  totalCols,
  totalRows,
  gameState,
  equippedProps = [],
}: Character3DWebGLProps) {
  const groupRef = useRef<THREE.Group>(null);
  const bodyRef = useRef<THREE.Mesh>(null);
  const targetPos = useRef(new THREE.Vector3());
  const currentPos = useRef(new THREE.Vector3());
  const elapsed = useRef(0);
  const collisionFlash = useRef(0);
  const collisionWobble = useRef(0);
  const [initialized, setInitialized] = useState(false);

  const currentRotY = useRef(0);
  const targetRotY = useRef(0);
  const timeSinceStop = useRef(0);
  const lastDirection = useRef<Direction | null>(null);

  // Animation sub-phases
  const [flyAwayPhase, setFlyAwayPhase] = useState<FlyAwayPhase>('idle');
  const [entrancePhase, setEntrancePhase] = useState<EntrancePhase>('hidden');
  const phaseStartTime = useRef(0); // tracks when current phase began

  const config = CHARACTER_FACE_COLORS[characterId] || CHARACTER_FACE_COLORS.default_hero;
  const charSize = 0.6 * config.scale;
  const baseY = FLOOR_HEIGHT + charSize / 2 + 0.02;
  const half = charSize / 2;

  const gridToWorld = (pos: Position): THREE.Vector3 => {
    const x = (pos[0] - (totalCols - 1) / 2) * TILE_SIZE;
    const z = (pos[1] - (totalRows - 1) / 2) * TILE_SIZE;
    return new THREE.Vector3(x, baseY, z);
  };

  useEffect(() => {
    const worldPos = gridToWorld(position);
    targetPos.current.copy(worldPos);
    if (!initialized) {
      currentPos.current.copy(worldPos);
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, totalCols, totalRows]);

  // Collision: trigger flash + wobble
  useEffect(() => {
    if (hasCollided) {
      collisionFlash.current = 0.5;
      collisionWobble.current = 1.0;
    }
  }, [hasCollided]);

  // Helper to set phase and record its start time
  const setEntrancePhaseWithTime = (phase: EntrancePhase) => {
    phaseStartTime.current = elapsed.current;
    setEntrancePhase(phase);
  };
  const setFlyAwayPhaseWithTime = (phase: FlyAwayPhase) => {
    phaseStartTime.current = elapsed.current;
    setFlyAwayPhase(phase);
  };

  // Entrance animation sequence (matching 2D: hidden -> appear -> hop1 -> hop2 -> landing)
  useEffect(() => {
    if (gameState === 'entering') {
      setEntrancePhaseWithTime('hidden');
      const timers: ReturnType<typeof setTimeout>[] = [];

      // T+500ms: Appear
      timers.push(setTimeout(() => setEntrancePhaseWithTime('appear'), 500));
      // T+700ms: First hop
      timers.push(setTimeout(() => setEntrancePhaseWithTime('hop1'), 700));
      // T+1000ms: Second hop
      timers.push(setTimeout(() => setEntrancePhaseWithTime('hop2'), 1000));
      // T+1300ms: Land
      timers.push(setTimeout(() => setEntrancePhaseWithTime('landing'), 1300));

      return () => timers.forEach(clearTimeout);
    } else {
      setEntrancePhase('landing');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Exit animation sequence (matching 2D: hop -> spin -> launch -> gone)
  useEffect(() => {
    if (gameState === 'exiting') {
      setFlyAwayPhaseWithTime('idle');
      const timers: ReturnType<typeof setTimeout>[] = [];

      // T+200ms: Celebration hop
      timers.push(setTimeout(() => setFlyAwayPhaseWithTime('hop'), 200));
      // T+600ms: Start spinning (scale up)
      timers.push(setTimeout(() => setFlyAwayPhaseWithTime('spin'), 600));
      // T+1000ms: Launch upward (fly away, shrink, fade)
      timers.push(setTimeout(() => setFlyAwayPhaseWithTime('launch'), 1000));
      // T+1800ms: Gone
      timers.push(setTimeout(() => setFlyAwayPhaseWithTime('gone'), 1800));

      return () => timers.forEach(clearTimeout);
    } else {
      setFlyAwayPhase('idle');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Reset elapsed on state change
  useEffect(() => {
    elapsed.current = 0;
  }, [gameState]);

  // Arm refs for walk swing
  const leftArmRef = useRef<THREE.Mesh>(null);
  const rightArmRef = useRef<THREE.Mesh>(null);

  // Sparkle trail refs for exit animation
  const sparkleRefs = useRef<THREE.Mesh[]>([]);

  useFrame((state, delta) => {
    if (!groupRef.current || !bodyRef.current) return;
    elapsed.current += delta;
    const t = elapsed.current;

    // Smooth position lerp
    currentPos.current.lerp(targetPos.current, Math.min(delta * 12, 1));

    // Collision wobble decay
    if (collisionWobble.current > 0) {
      collisionWobble.current = Math.max(0, collisionWobble.current - delta * 3);
    }

    // Collision flash decay
    if (collisionFlash.current > 0) {
      collisionFlash.current = Math.max(0, collisionFlash.current - delta * 3);
    }

    // Direction logic
    if (isMoving && direction) {
      lastDirection.current = direction;
      timeSinceStop.current = 0;
      targetRotY.current =
        direction === 'left' ? -Math.PI / 2 :
        direction === 'right' ? Math.PI / 2 :
        direction === 'up' ? Math.PI :
        0;
    } else {
      timeSinceStop.current += delta;
      if (timeSinceStop.current > 0.6) {
        targetRotY.current = 0;
      }
    }

    // Phase-local time (time since current phase started)
    const pt = t - phaseStartTime.current;

    // State-based animations
    switch (gameState) {
      case 'entering': {
        groupRef.current.position.copy(currentPos.current);

        // Entrance phases (using phase-local time `pt`)
        if (entrancePhase === 'hidden') {
          groupRef.current.scale.setScalar(0);
          groupRef.current.position.y = currentPos.current.y + 2;
        } else if (entrancePhase === 'appear') {
          // Scale in with bounce over ~200ms
          const progress = Math.min(pt * 5, 1);
          const bounce = 1 - Math.pow(1 - progress, 3);
          groupRef.current.scale.setScalar(bounce * 0.9);
          groupRef.current.position.y = currentPos.current.y + (1 - bounce) * 2;
        } else if (entrancePhase === 'hop1') {
          // First hop: bounce up over ~300ms
          groupRef.current.scale.setScalar(1);
          const hopT = Math.min(pt * 3.3, 1);
          const hopHeight = Math.sin(hopT * Math.PI) * 0.4;
          groupRef.current.position.y = currentPos.current.y + hopHeight;
          // Squash on landing
          if (hopT > 0.8) {
            const squashT = (hopT - 0.8) / 0.2;
            bodyRef.current.scale.set(1 + squashT * 0.15, 1 - squashT * 0.1, 1 + squashT * 0.15);
          } else {
            bodyRef.current.scale.set(1, 1, 1);
          }
        } else if (entrancePhase === 'hop2') {
          // Second hop: higher bounce over ~300ms
          groupRef.current.scale.setScalar(1);
          const hopT = Math.min(pt * 3.3, 1);
          const hopHeight = Math.sin(hopT * Math.PI) * 0.6;
          groupRef.current.position.y = currentPos.current.y + hopHeight;
          // Slight rotation during hop
          groupRef.current.rotation.y = Math.sin(hopT * Math.PI) * 0.3;
        } else {
          // Landing: settle
          groupRef.current.scale.setScalar(1);
          groupRef.current.position.y = currentPos.current.y;
          // Squash-stretch on landing
          const landT = Math.min(pt * 4, 1);
          if (landT < 0.3) {
            bodyRef.current.scale.set(1.15, 0.85, 1.15);
          } else {
            const recover = Math.min((landT - 0.3) / 0.7, 1);
            bodyRef.current.scale.set(
              1.15 - recover * 0.15,
              0.85 + recover * 0.15,
              1.15 - recover * 0.15
            );
          }
        }
        // Don't apply rotation logic during entrance
        const rotSpeed = 4;
        currentRotY.current += (0 - currentRotY.current) * Math.min(delta * rotSpeed, 1);
        groupRef.current.rotation.z = 0;
        break;
      }

      case 'won': {
        groupRef.current.position.copy(currentPos.current);
        // Celebration: jump + spin (matching 2D celebration hop)
        const jump = Math.abs(Math.sin(t * 6)) * 0.5;
        groupRef.current.position.y = currentPos.current.y + jump;
        groupRef.current.rotation.y = t * 4;
        groupRef.current.scale.setScalar(1);
        // Happy squash-stretch
        const celebStretch = 1 + Math.sin(t * 12) * 0.05;
        bodyRef.current.scale.set(1 / celebStretch, celebStretch, 1 / celebStretch);
        break;
      }

      case 'exiting': {
        groupRef.current.position.copy(currentPos.current);

        switch (flyAwayPhase) {
          case 'idle':
            groupRef.current.scale.setScalar(1);
            break;
          case 'hop': {
            // Quick celebration hop over ~400ms (phase-local time)
            const hopT = Math.min(pt * 2.5, 1);
            const hopH = Math.sin(hopT * Math.PI) * 0.5;
            groupRef.current.position.y = currentPos.current.y + hopH;
            groupRef.current.scale.setScalar(1);
            break;
          }
          case 'spin': {
            // Scale up + spin over ~400ms (phase-local time)
            const spinT = Math.min(pt * 2.5, 1);
            const spinScale = 1 + spinT * 0.2;
            groupRef.current.scale.setScalar(spinScale);
            groupRef.current.rotation.y = spinT * Math.PI * 2;
            // Slight lift during spin
            groupRef.current.position.y = currentPos.current.y + spinT * 0.3;
            break;
          }
          case 'launch': {
            // Launch upward over ~800ms (phase-local time)
            const launchT = Math.min(pt * 1.25, 1);
            const eased = 1 - Math.pow(1 - launchT, 2);
            const launchScale = Math.max(0.1, 1.2 - eased * 0.9);
            groupRef.current.scale.setScalar(launchScale);
            groupRef.current.position.y = currentPos.current.y + 0.3 + eased * 8;
            groupRef.current.rotation.y += delta * 15;
            break;
          }
          case 'gone': {
            groupRef.current.scale.setScalar(0);
            groupRef.current.position.y = currentPos.current.y + 12;
            break;
          }
        }

        // Animate sparkle trail
        if (flyAwayPhase === 'spin' || flyAwayPhase === 'launch') {
          sparkleRefs.current.forEach((spark, i) => {
            if (!spark) return;
            const angle = t * 8 + (i / 6) * Math.PI * 2;
            const radius = 0.3 + Math.sin(t * 4 + i) * 0.2;
            spark.position.set(
              Math.cos(angle) * radius,
              Math.sin(t * 6 + i * 0.5) * 0.3 + i * 0.1,
              Math.sin(angle) * radius
            );
            const sparkScale = Math.sin(t * 10 + i * 2) * 0.5 + 0.5;
            spark.scale.setScalar(sparkScale * 0.06);
            spark.visible = true;
          });
        } else {
          sparkleRefs.current.forEach(spark => {
            if (spark) spark.visible = false;
          });
        }
        break;
      }

      case 'lost': {
        groupRef.current.position.copy(currentPos.current);
        // Impact + wobble + deflate (matching 2D death phases)
        if (t < 0.3) {
          // Impact: shake
          const shake = Math.sin(t * 30) * 0.2 * (1 - t / 0.3);
          groupRef.current.position.x = currentPos.current.x + shake;
          groupRef.current.rotation.z = shake;
          groupRef.current.scale.setScalar(1);
        } else if (t < 1.0) {
          // Deflate: shrink + tilt
          const deflateT = (t - 0.3) / 0.7;
          const shrink = Math.max(0.3, 1 - deflateT * 0.7);
          groupRef.current.scale.setScalar(shrink);
          groupRef.current.rotation.z = Math.sin(t * 8) * 0.2 * (1 - deflateT);
          // Squish down
          bodyRef.current.scale.set(1 + deflateT * 0.3, 1 - deflateT * 0.4, 1 + deflateT * 0.3);
        } else {
          // Faded/small
          groupRef.current.scale.setScalar(0.3);
          groupRef.current.rotation.z = 0;
        }
        break;
      }

      case 'playing':
      default: {
        groupRef.current.position.copy(currentPos.current);

        // Wall-hit wobble on collision
        if (collisionWobble.current > 0) {
          const wobbleIntensity = collisionWobble.current;
          const wobble = Math.sin(t * 25) * 0.15 * wobbleIntensity;
          groupRef.current.rotation.z = wobble;
          // Squash effect on hit
          bodyRef.current.scale.set(
            1 + wobbleIntensity * 0.1,
            1 - wobbleIntensity * 0.08,
            1 + wobbleIntensity * 0.1
          );
        } else if (isMoving) {
          // Walk animation: vertical bounce + body tilt (from showcase)
          const walkBounce = Math.abs(Math.sin(t * 10)) * 0.12;
          groupRef.current.position.y = currentPos.current.y + walkBounce;
          const walkTilt = Math.sin(t * 10) * 0.08;
          groupRef.current.rotation.z = walkTilt;

          // Squash/stretch during movement
          const stretchAxis = direction === 'left' || direction === 'right' ? 'x' : 'z';
          const stretch = 1 + Math.sin(t * 20) * 0.08;
          const squash = 1 / Math.sqrt(stretch);
          if (stretchAxis === 'x') {
            bodyRef.current.scale.set(stretch, squash, squash);
          } else {
            bodyRef.current.scale.set(squash, squash, stretch);
          }

          // Arm swing during walk
          if (leftArmRef.current && rightArmRef.current) {
            const swing = Math.sin(t * 10) * 0.4;
            leftArmRef.current.rotation.x = swing;
            rightArmRef.current.rotation.x = -swing;
          }
        } else {
          // Idle: gentle bob + breathing (showcase idle animation)
          const idleBob = Math.sin(t * 2) * 0.03;
          groupRef.current.position.y = currentPos.current.y + idleBob;
          const breathe = 1 + Math.sin(t * 3) * 0.03;
          bodyRef.current.scale.set(1 - (breathe - 1), breathe, 1 - (breathe - 1));
          groupRef.current.rotation.z = 0;

          // Reset arm rotation when idle
          if (leftArmRef.current && rightArmRef.current) {
            leftArmRef.current.rotation.x *= 0.9;
            rightArmRef.current.rotation.x *= 0.9;
          }
        }

        // Scale recovery
        if (groupRef.current.scale.x < 0.95) {
          const recover = Math.min(groupRef.current.scale.x + delta * 4, 1);
          groupRef.current.scale.setScalar(recover);
        } else if (groupRef.current.scale.x !== 1) {
          groupRef.current.scale.setScalar(1);
        }
        break;
      }
    }

    // Apply rotation (except during specific phases)
    if (gameState !== 'entering' && gameState !== 'exiting') {
      const rotSpeed = isMoving ? 10 : 4;
      currentRotY.current += (targetRotY.current - currentRotY.current) * Math.min(delta * rotSpeed, 1);
      groupRef.current.rotation.y = currentRotY.current;
    }
  });

  const bodyColor = useMemo(() => new THREE.Color(config.front), [config.front]);
  const expression = getExpression(gameState, flyAwayPhase, hasCollided);
  const faceScale = charSize;

  return (
    <group ref={groupRef} scale={0}>
      {/* Body with wobble material for jelly physics */}
      <RoundedBox
        ref={bodyRef}
        args={[charSize, charSize, charSize]}
        radius={charSize * 0.22}
        smoothness={4}
        castShadow
      >
        <MeshWobbleMaterial
          color={bodyColor}
          metalness={0.15}
          roughness={0.35}
          factor={0.1}
          speed={2}
        />
      </RoundedBox>

      {/* Face component with expression */}
      <Face
        expression={expression}
        eyeColor="#1a1a2e"
        mouthColor="#2a1a1a"
        scale={faceScale}
      />

      {/* Arms with walk swing */}
      <RoundedBox
        ref={leftArmRef}
        args={[charSize * 0.2, charSize * 0.4, charSize * 0.2]}
        radius={charSize * 0.1}
        position={[-charSize * 0.6, -charSize * 0.2, 0]}
        rotation={[0, 0, 0.2]}
        castShadow
      >
        <meshStandardMaterial color={config.front} />
      </RoundedBox>
      <RoundedBox
        ref={rightArmRef}
        args={[charSize * 0.2, charSize * 0.4, charSize * 0.2]}
        radius={charSize * 0.1}
        position={[charSize * 0.6, -charSize * 0.2, 0]}
        rotation={[0, 0, -0.2]}
        castShadow
      >
        <meshStandardMaterial color={config.front} />
      </RoundedBox>

      {/* Equipped accessories */}
      <group scale={charSize}>
        {equippedProps.includes('wizard-hat') && <WizardHat />}
        {equippedProps.includes('ice-crown') && <WizardHat />}
        {equippedProps.includes('viking-helmet') && <WizardHat />}
        {equippedProps.includes('gamer-headphones') && <WizardHat />}
        {equippedProps.includes('jetpack') && <RocketJetpack />}
        {equippedProps.includes('shield-generator') && <RocketJetpack />}
        {equippedProps.includes('dragon-wings') && <RocketJetpack />}
        {equippedProps.includes('cool-glasses') && <CoolGlasses />}
        {equippedProps.includes('hipster-glasses') && <CoolGlasses />}
      </group>

      {/* Entrance portal glow (sphere that scales down like 2D portal) */}
      {gameState === 'entering' && entrancePhase !== 'hidden' && entrancePhase !== 'landing' && (
        <mesh>
          <sphereGeometry args={[charSize * 1.5, 16, 16]} />
          <meshBasicMaterial
            color="#64c8ff"
            transparent
            opacity={entrancePhase === 'appear' ? 0.6 : 0.3}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Fly-away sparkle trail (6 sparkles like 2D version) */}
      {gameState === 'exiting' && (
        <>
          {[...Array(6)].map((_, i) => (
            <mesh
              key={`sparkle-${i}`}
              ref={(el) => { if (el) sparkleRefs.current[i] = el; }}
              visible={false}
            >
              <sphereGeometry args={[1, 8, 8]} />
              <meshBasicMaterial
                color={i % 2 === 0 ? '#FFD700' : '#ffffff'}
                transparent
                opacity={0.9}
              />
            </mesh>
          ))}
        </>
      )}

      {/* Shadow beneath */}
      <mesh position={[0, -half + 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[charSize * 0.4, 16]} />
        <meshBasicMaterial color="black" transparent opacity={0.2} />
      </mesh>
    </group>
  );
}
